import {
  Flex,
  CircleHelpIcon,
  Link,
  Paragraph,
  SparklesIcon,
  Strong,
  WarningIcon,
} from '@buffer-mono/popcorn'

import {
  selectContentGenerationStatus,
  selectContentGenerationConfig,
  selectContentGenerationSelection,
} from '~publish/legacy/ai/state/slice'
import { useAppSelector } from '~publish/legacy/store'

import styles from './Footer.module.css'

import type { JSX } from 'react'

/**
 *
 */
const TIPS = {
  withPrompt: {
    Icon: SparklesIcon,
    highlight: 'Pro Tip:',
    text: 'Select parts of your existing copy to refine using the AI Assistant!',
    link: {
      label: 'Learn more',
      url: 'https://support.buffer.com/articles/using-buffers-ai-assistant-qbBulfw9sn?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more',
    },
  },
  emptyPrompt: {
    Icon: CircleHelpIcon,
    highlight: null,
    text: 'Learn more about',
    link: {
      label: 'creating great prompts and the known limitations of AI.',
      url: 'https://support.buffer.com/articles/using-buffers-ai-assistant-qbBulfw9sn?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more',
    },
  },
  withResults: {
    Icon: WarningIcon,
    highlight: null,
    text: 'AI responses can be inaccurate or misleading. Always review before publishing.',
    link: {
      label: 'Learn more.',
      url: 'https://support.buffer.com/articles/using-buffers-ai-assistant-qbBulfw9sn?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more',
    },
  },
}

/**
 *
 */
function Footer(): JSX.Element | null {
  const {
    contentGenerationInProgress,
    contentGenerationError,
    contentGenerationResults,
  } = useAppSelector((state) => selectContentGenerationStatus(state))

  const { prompt } = useAppSelector((state) =>
    selectContentGenerationConfig(state),
  )

  const contentGenerationSelection = useAppSelector((state) =>
    selectContentGenerationSelection(state),
  )

  let tip = prompt ? 'withPrompt' : 'emptyPrompt'
  if (contentGenerationResults.length > 0) {
    tip = 'withResults'
  }

  const tipConfig =
    Object.entries(TIPS).find(([tipKey]) => tipKey === tip)?.[1] ?? null
  const { Icon, highlight, text, link } = tipConfig || {}
  const { label, url } = link || {}

  const showTip =
    !contentGenerationInProgress &&
    !contentGenerationError &&
    !contentGenerationSelection

  return showTip ? (
    <Flex gap="xs" className={styles.footer}>
      {Icon && (
        <div className={styles.iconWrapper}>
          <Icon size="small" />
        </div>
      )}
      <Paragraph>
        {highlight && <Strong>{`${highlight} `}</Strong>}
        {text}
        {label && url && (
          <>
            {' '}
            <Link
              href={url || ''}
              external
              data-testid="ai-assistant-footer-tip-link"
            >
              {`${label}`}
            </Link>
          </>
        )}
      </Paragraph>
    </Flex>
  ) : null
}

export { Footer }
